import { useCallback } from "react";
import type { UseQueryResult } from "@tanstack/react-query";
import { useLocalization } from "@/context/localization-context";
import type { ChartSeries } from "../../components/time-series-chart";
import { formatCount, formatMs, formatMsTick } from "../../format";
import { NEUTRAL, SERIES, SERIES_LIGHT, STATUS } from "../../palette";
import type { MetricSeries } from "../../types";
import { counterTotal, counterValues, hasValues, histTotal, histValues } from "../pipeline/helpers";
import { MetricChartCard, SeriesTotals } from "../pipeline/shared";

/** Window charts of the camera tab, from the in-process metrics of the backend. */
export function CameraCharts({ query }: { query: UseQueryResult<MetricSeries> }) {
  const { t } = useLocalization();
  const windowText = t(`monitoring.window.long.${query.data?.window ?? "6h"}`);

  const triggers = useCallback(
    (m: MetricSeries): ChartSeries[] => {
      const triggered = counterValues(m, "capture.triggered");
      const deviceError = counterValues(m, "capture.device_error");
      const mock = counterValues(m, "capture.mock");
      return [
        // a device error is a trigger the camera answered with success=false: shown on its own, not twice
        {
          key: "triggered",
          label: t("monitoring.cameras.series.triggered"),
          color: NEUTRAL.mid,
          type: "bar",
          stackId: "capture",
          values: triggered.map((v, i) => Math.max(0, v - (deviceError[i] ?? 0))),
        },
        { key: "device_error", label: t("monitoring.cameras.series.deviceError"), color: STATUS.serious, type: "bar", stackId: "capture", values: deviceError },
        { key: "no_camera", label: t("monitoring.cameras.series.noCamera"), color: STATUS.warning, type: "bar", stackId: "capture", values: counterValues(m, "capture.no_camera") },
        { key: "trigger_failed", label: t("monitoring.cameras.series.triggerFailed"), color: STATUS.critical, type: "bar", stackId: "capture", values: counterValues(m, "capture.trigger_failed") },
        ...(hasValues(mock)
          ? [{ key: "mock", label: t("monitoring.cameras.series.mock"), color: NEUTRAL.soft, type: "bar" as const, stackId: "capture", values: mock }]
          : []),
      ];
    },
    [t],
  );

  const uploads = useCallback(
    (m: MetricSeries): ChartSeries[] => {
      const service = counterValues(m, "upload.service_photo");
      return [
        { key: "upload_ok", label: t("monitoring.cameras.series.uploadOk"), color: NEUTRAL.mid, type: "bar", stackId: "upload", values: counterValues(m, "upload.ok") },
        { key: "upload_rejected", label: t("monitoring.cameras.series.uploadRejected"), color: STATUS.warning, type: "bar", stackId: "upload", values: counterValues(m, "upload.rejected") },
        { key: "upload_failed", label: t("monitoring.cameras.series.uploadFailed"), color: STATUS.critical, type: "bar", stackId: "upload", values: counterValues(m, "upload.failed") },
        ...(hasValues(service)
          ? [{ key: "service_photo", label: t("monitoring.cameras.series.servicePhoto"), color: NEUTRAL.soft, type: "bar" as const, stackId: "upload", values: service }]
          : []),
      ];
    },
    [t],
  );

  const uploadDelay = useCallback(
    (m: MetricSeries): ChartSeries[] => [
      { key: "delay_p95", label: t("monitoring.series.p95"), color: SERIES[0], values: histValues(m, "upload.delay_ms", "p95") },
      { key: "delay_p50", label: t("monitoring.series.p50"), color: SERIES_LIGHT, values: histValues(m, "upload.delay_ms", "p50") },
    ],
    [t],
  );

  const backendSide = useCallback(
    (m: MetricSeries): ChartSeries[] => [
      { key: "trigger_rpc_p95", label: t("monitoring.cameras.series.triggerRpc"), color: SERIES[0], values: histValues(m, "capture.trigger_rpc_ms", "p95") },
      { key: "s3_put_p95", label: t("monitoring.cameras.series.s3Put"), color: SERIES[1], values: histValues(m, "upload.s3_put_ms", "p95") },
    ],
    [t],
  );

  return (
    <div className="grid grid-cols-1 gap-4 xl:grid-cols-2">
      <MetricChartCard
        title={t("monitoring.cameras.chart.triggers")}
        description={t("monitoring.cameras.chart.triggersDescription", { window: windowText })}
        query={query}
        build={triggers}
        format={formatCount}
        integer
        footer={(m) => (
          <SeriesTotals
            items={[
              { label: t("monitoring.cameras.series.triggered"), value: formatCount(counterTotal(m, "capture.triggered")) },
              { label: t("monitoring.cameras.series.deviceError"), value: formatCount(counterTotal(m, "capture.device_error")), tone: counterTotal(m, "capture.device_error") ? "warn" : "neutral" },
              { label: t("monitoring.cameras.series.noCamera"), value: formatCount(counterTotal(m, "capture.no_camera")), tone: counterTotal(m, "capture.no_camera") ? "warn" : "neutral" },
              { label: t("monitoring.cameras.series.triggerFailed"), value: formatCount(counterTotal(m, "capture.trigger_failed")), tone: counterTotal(m, "capture.trigger_failed") ? "nok" : "neutral" },
            ]}
          />
        )}
      />
      <MetricChartCard
        title={t("monitoring.cameras.chart.uploads")}
        description={t("monitoring.cameras.chart.uploadsDescription", { window: windowText })}
        query={query}
        build={uploads}
        format={formatCount}
        integer
        footer={(m) => (
          <SeriesTotals
            items={[
              { label: t("monitoring.cameras.series.uploadOk"), value: formatCount(counterTotal(m, "upload.ok")) },
              { label: t("monitoring.cameras.series.uploadRejected"), value: formatCount(counterTotal(m, "upload.rejected")), tone: counterTotal(m, "upload.rejected") ? "warn" : "neutral" },
              { label: t("monitoring.cameras.series.uploadFailed"), value: formatCount(counterTotal(m, "upload.failed")), tone: counterTotal(m, "upload.failed") ? "nok" : "neutral" },
              { label: t("monitoring.cameras.series.servicePhoto"), value: formatCount(counterTotal(m, "upload.service_photo")) },
            ]}
          />
        )}
      />
      <MetricChartCard
        title={t("monitoring.cameras.chart.uploadDelay")}
        description={t("monitoring.cameras.chart.uploadDelayDescription", { window: windowText })}
        query={query}
        build={uploadDelay}
        format={formatMs}
        tickFormat={formatMsTick}
        footer={(m) => <HistTotals metrics={m} name="upload.delay_ms" />}
      />
      <MetricChartCard
        title={t("monitoring.cameras.chart.backendSide")}
        description={t("monitoring.cameras.chart.backendSideDescription", { window: windowText })}
        query={query}
        build={backendSide}
        format={formatMs}
        tickFormat={formatMsTick}
        footer={(m) => (
          <div className="flex flex-col gap-1">
            <HistTotals metrics={m} name="capture.trigger_rpc_ms" label={t("monitoring.cameras.series.triggerRpcShort")} />
            <HistTotals metrics={m} name="upload.s3_put_ms" label={t("monitoring.cameras.series.s3PutShort")} />
          </div>
        )}
      />
    </div>
  );
}

/** "n 120 · p50 230 ms · p95 1.2 s · max 4 s" of the whole window */
function HistTotals({ metrics, name, label }: { metrics: MetricSeries; name: string; label?: string }) {
  const { t } = useLocalization();
  const hist = histTotal(metrics, name);
  return (
    <SeriesTotals
      title={label}
      items={[
        { label: t("monitoring.pipeline.col.n"), value: formatCount(hist?.n ?? 0) },
        { label: t("monitoring.series.p50"), value: formatMs(hist?.p50) },
        { label: t("monitoring.series.p95"), value: formatMs(hist?.p95) },
        { label: t("monitoring.pipeline.col.max"), value: formatMs(hist?.max) },
      ]}
    />
  );
}
